/**
 * Panel - Resizable, collapsible side panel
 *
 * Can be used standalone or via AppContainer's leftPanel/rightPanel props.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { PanelConfig } from './LayoutContext';

export interface PanelProps {
  children: React.ReactNode;
  title?: string;
  icon?: string;
  position: 'left' | 'right';
  /** Controlled collapsed state */
  isCollapsed?: boolean;
  /** Called when collapsed state should change */
  onToggle?: () => void;
  /** Called when panel is resized */
  onResize?: (width: number) => void;
  /** Panel configuration (from LayoutContext) */
  config?: PanelConfig;
  /** Fixed width (uncontrolled mode) */
  width?: number;
  className?: string;
  /** Show resize handle */
  resizable?: boolean;
  /** Collapse to icon-only mode */
  collapsible?: boolean;
  /** Default width when rendered (alias for width) */
  defaultWidth?: number;
}

export const Panel: React.FC<PanelProps> = ({
  children,
  title,
  icon,
  position,
  isCollapsed: controlledCollapsed,
  onToggle,
  onResize,
  config,
  width: fixedWidth,
  className = '',
  resizable = true,
  collapsible = true,
}) => {
  // Uncontrolled mode
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsed = controlledCollapsed ?? internalCollapsed;

  const width = config?.width ?? fixedWidth ?? 280;
  const minWidth = config?.minWidth ?? 200;
  const maxWidth = config?.maxWidth ?? 500;

  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(width);

  const handleToggle = useCallback(() => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalCollapsed(prev => !prev);
    }
  }, [onToggle]);

  // Resize handlers
  const startResize = useCallback((e: React.MouseEvent) => {
    if (!resizable) return;
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [resizable, width]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = position === 'left'
        ? e.clientX - startXRef.current
        : startXRef.current - e.clientX;

      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidthRef.current + delta));

      if (onResize) {
        onResize(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, position, minWidth, maxWidth, onResize]);

  // Collapsed state - just show icon bar
  if (isCollapsed) {
    return (
      <div
        ref={panelRef}
        className={`panel panel-collapsed panel-${position} ${className}`}
        style={{
          width: 40,
          height: '100%',
          backgroundColor: '#2c3e50',
          borderRight: position === 'left' ? '1px solid #34495e' : undefined,
          borderLeft: position === 'right' ? '1px solid #34495e' : undefined,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 10,
        }}
      >
        {collapsible && (
          <button
            onClick={handleToggle}
            title={title || 'Expand panel'}
            style={{
              width: 28,
              height: 28,
              backgroundColor: '#34495e',
              border: 'none',
              borderRadius: 4,
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              marginBottom: 10,
            }}
          >
            {position === 'left' ? '›' : '‹'}
          </button>
        )}
        {icon && (
          <div
            title={title}
            style={{
              fontSize: 18,
              cursor: collapsible ? 'pointer' : 'default',
            }}
            onClick={collapsible ? handleToggle : undefined}
          >
            {icon}
          </div>
        )}

        {/* Side Toggle Grabber (Collapsed State) */}
        {collapsible && (
          <button
            onClick={handleToggle}
            title="Expand panel"
            style={{
              position: 'absolute',
              top: '50%',
              [position === 'left' ? 'right' : 'left']: -14, // push outside the 40px bar
              transform: 'translateY(-50%)',
              width: 14,
              height: 40,
              backgroundColor: '#2c3e50',
              border: '1px solid #1a252f',
              borderLeft: position === 'right' ? 'none' : '1px solid #1a252f',
              borderRight: position === 'left' ? 'none' : '1px solid #1a252f',
              borderRadius: position === 'left' ? '0 4px 4px 0' : '4px 0 0 4px',
              color: '#bdc3c7',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              zIndex: 101, // above canvas
              fontSize: '10px'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#34495e')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2c3e50')}
          >
            {position === 'left' ? '▶' : '◀'}
          </button>
        )}
      </div>
    );
  }

  // Expanded state
  return (
    <div
      ref={panelRef}
      className={`panel panel-expanded panel-${position} ${className} ${isResizing ? 'panel-resizing' : ''}`}
      style={{
        width,
        flex: 1,
        height: '100%',
        backgroundColor: '#34495e',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      {(title || collapsible) && (
        <div
          className="panel-header"
          style={{
            padding: '12px 15px',
            borderBottom: '1px solid #2c3e50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#2c3e50',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
            {title && (
              <span
                style={{
                  fontWeight: 'bold',
                  fontSize: 14,
                  color: 'white',
                }}
              >
                {title}
              </span>
            )}
          </div>
          {collapsible && (
            <button
              onClick={handleToggle}
              title="Collapse panel"
              style={{
                width: 24,
                height: 24,
                backgroundColor: 'transparent',
                border: 'none',
                color: '#95a5a6',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                borderRadius: 4,
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'white')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#95a5a6')}
            >
              {position === 'left' ? '‹' : '›'}
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div
        className="panel-content"
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 15,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </div>

      {/* Resize Handle */}
      {resizable && (
        <div
          className="panel-resize-handle"
          onMouseDown={startResize}
          style={{
            position: 'absolute',
            top: 0,
            [position === 'left' ? 'right' : 'left']: 0,
            width: 6,
            height: '100%',
            cursor: position === 'left' ? 'ew-resize' : 'ew-resize',
            backgroundColor: isResizing ? '#3498db' : 'transparent',
            transition: 'background-color 0.2s',
            zIndex: 100,
          }}
          onMouseEnter={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = '#34495e';
          }}
          onMouseLeave={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = 'transparent';
          }}
        />
      )}

      {/* Side Toggle Grabber (Expanded State) */}
      {collapsible && (
        <button
          onClick={handleToggle}
          title="Collapse panel"
          style={{
            position: 'absolute',
            top: '50%',
            [position === 'left' ? 'right' : 'left']: 0,
            transform: `translate(${position === 'left' ? '50%' : '-50%'}, -50%)`,
            width: 14,
            height: 40,
            backgroundColor: '#2c3e50',
            border: '1px solid #1a252f',
            borderRadius: position === 'left' ? '0 4px 4px 0' : '4px 0 0 4px',
            color: '#bdc3c7',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            zIndex: 101, // above resize handle
            fontSize: '10px'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#34495e')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2c3e50')}
        >
          {position === 'left' ? '◀' : '▶'}
        </button>
      )}
    </div>
  );
};
